import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { LexiconSet, RoundState, ScoreBreakdown } from '@polyglot/engine';
import { MODES, scoreRound, targetOf } from '@polyglot/engine';
import { formatPercent, formatScore, formatSeconds } from '../lib/format';

export interface ResultsOpponent {
  label: string;
  isBot: boolean;
  state: RoundState;
}

function speedPct(s: ScoreBreakdown): number {
  return s.speed.bonusBpDenominator === 0
    ? 0
    : s.speed.bonusBpNumerator / s.speed.bonusBpDenominator / 10000;
}

export function Breakdown({
  state,
  score,
  title,
}: {
  state: RoundState;
  score: ScoreBreakdown;
  title: string;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const target = targetOf(state);
  const mode = MODES[state.config.mode];
  const lang = score.language;
  return (
    <section className="card breakdown">
      <h2>{title}</h2>
      <p className="total">
        <span className="total-value">{formatScore(score.totalCents, locale)}</span>{' '}
        <span className="muted">{t('results.points')}</span>
      </p>
      <dl className="rows">
        <div>
          <dt>{t('results.base')}</dt>
          <dd>{score.basePoints}</dd>
        </div>
        {lang.kind !== 'none' && (
          <div>
            <dt>
              {lang.contract ? t(`contracts.${lang.contract}.name`) : t('contracts.diversity.name')}{' '}
              <span className={`badge ${lang.fulfilled ? 'ok' : 'ko'}`}>
                {lang.fulfilled ? t('results.fulfilled') : t('results.missed')}
              </span>
            </dt>
            <dd>+{formatPercent(lang.appliedBp / 10000, locale)}</dd>
          </div>
        )}
        {mode.speedBonus && (
          <div>
            <dt>{t('results.speedBonus')}</dt>
            <dd>+{formatPercent(speedPct(score), locale, 2)}</dd>
          </div>
        )}
        <div>
          <dt>{t('results.officialTime')}</dt>
          <dd>
            {state.endedAtMs !== null ? formatSeconds(state.endedAtMs, locale) : '—'} ·{' '}
            {t(`results.endReason.${state.endReason ?? 'time_up'}`)}
          </dd>
        </div>
        {target !== null && (
          <div>
            <dt>{t('results.target')}</dt>
            <dd>{t('game.wordsOfTarget', { n: state.words.length, target })}</dd>
          </div>
        )}
      </dl>
      <div className="lang-counters">
        {score.perLanguage.map((c) => (
          <span key={c.lang} className={`counter lang-${c.lang}`}>
            <strong>{c.lang.toUpperCase()}</strong> {t('game.wordsCount', { count: c.words })} ·{' '}
            {c.points} {t('results.pts')}
          </span>
        ))}
      </div>
      <p className="explain">{explanation(state, score, t, locale)}</p>
      <ol className="word-list final">
        {state.words.map((w) => (
          <li key={w.seq} className="word-chip">
            <span className={`lang-tag lang-${w.lang}`}>{w.lang.toUpperCase()}</span>
            <span className="word-display">{w.display}</span>
            <span className="word-points">+{w.points}</span>
            <span className="muted small">{formatSeconds(w.atMs, locale)}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

type TFn = (k: string, o?: Record<string, unknown>) => string;

function explanation(state: RoundState, s: ScoreBreakdown, t: TFn, locale: string): string {
  const parts: string[] = [t('results.explainBase', { base: s.basePoints })];
  const lang = s.language;
  if (lang.kind === 'contract' && lang.contract === 'specialist')
    parts.push(t('results.explainSpecialist'));
  else if (lang.kind !== 'none') {
    parts.push(
      lang.fulfilled
        ? t('results.explainQuotaOk', { quota: lang.quotaPerLanguage, pct: lang.offeredBp / 100 })
        : t('results.explainQuotaKo', { quota: lang.quotaPerLanguage, pct: lang.offeredBp / 100 }),
    );
  }
  if (MODES[state.config.mode].speedBonus) {
    parts.push(
      s.speed.eligible
        ? t('results.explainSpeedOk', {
            time: formatSeconds(s.speed.officialTimeMs ?? 0, locale),
            pct: (speedPct(s) * 100).toFixed(2),
          })
        : t('results.explainSpeedKo'),
    );
  }
  parts.push(t('results.explainFormula'));
  return parts.join(' ');
}

export function ResultsView({
  player,
  opponent,
  lexicons,
  showSolutions,
  actions,
}: {
  player: RoundState;
  opponent?: ResultsOpponent | null;
  lexicons?: LexiconSet;
  showSolutions?: boolean;
  actions: React.ReactNode;
}) {
  const { t } = useTranslation();
  const mine = useMemo(() => scoreRound(player), [player]);
  const theirs = useMemo(() => (opponent ? scoreRound(opponent.state) : null), [opponent]);
  let verdict: string | null = null;
  if (theirs) {
    verdict =
      mine.totalCents > theirs.totalCents
        ? t('results.win')
        : mine.totalCents < theirs.totalCents
          ? t('results.lose')
          : t('results.tie');
  }
  const solutions = useMemo(() => {
    if (!showSolutions || !lexicons) return null;
    const found = new Set(player.words.map((w) => w.key));
    return player.config.languages.map((l) => {
      const lex = lexicons[l];
      if (!lex) return { lang: l, words: [] as string[] };
      const entries = lex
        .formableIndices(player.config.rack)
        .filter((i) => lex.tierAt(i) <= 2)
        .map((i) => ({ key: lex.keyAt(i), display: lex.displaysAt(i)[0]! }))
        .filter((e) => !found.has(e.key))
        .sort((a, b) => b.key.length - a.key.length || a.display.localeCompare(b.display))
        .slice(0, 24);
      return { lang: l, words: entries.map((e) => e.display) };
    });
  }, [showSolutions, lexicons, player]);

  return (
    <div className="results">
      {verdict && (
        <h1 className="verdict" role="status">
          {verdict}
        </h1>
      )}
      <Breakdown state={player} score={mine} title={t('results.you')} />
      {opponent && theirs && (
        <Breakdown
          state={opponent.state}
          score={theirs}
          title={
            opponent.isBot ? t('results.robotTitle', { name: opponent.label }) : opponent.label
          }
        />
      )}
      {theirs && mine.totalCents === theirs.totalCents && (
        <p className="muted">{t('results.tieExplain')}</p>
      )}
      <div className="results-actions">{actions}</div>
      {solutions && (
        <section className="card">
          <h2>{t('results.missedWords')}</h2>
          <p className="muted small">{t('results.missedWordsNote')}</p>
          {solutions.map((s) => (
            <p key={s.lang}>
              <span className={`lang-tag lang-${s.lang}`}>{s.lang.toUpperCase()}</span>{' '}
              {s.words.join(', ') || '—'}
            </p>
          ))}
        </section>
      )}
    </div>
  );
}
